import { Progress, theme } from 'antd'
import { observer } from 'mobx-react-lite'
import { TbDatabase } from 'react-icons/tb'

import { classNames } from '@/Ship/Helpers/classNames'

import { DISK_QUOTA_WARN_PERCENT } from '../Contracts/DiskNode'
import { formatBytes } from '../Helpers/diskFormat'
import { diskBrowserStore as store } from '../Stores/DiskBrowserStore'

import type { DiskQuotaLine } from '../Contracts/DiskNode'

import styles from './Disk.module.css'

/**
 * Яка квота важлива зараз: відкритого простору, якщо він має власну межу й
 * людина бачить його облік (свій диск або простір, яким керує), інакше —
 * «Мій диск».
 */
const currentLine = (): DiskQuotaLine | null => {
    const quota = store.quota
    if (quota === null) return null

    const storageUuid = store.currentStorage?.uuid
    const managed = quota.managed.find((line) => line.uuid === storageUuid && line.quotaBytes !== null)

    return managed ?? quota.personal
}

/**
 * Картка квоти (`GET /services/disk/quota`): «використано X з Y», смужка,
 * попередження з 80 % — тим самим порогом, з якого бек шле сповіщення.
 * Простір без власної межі показує лише використане.
 */
export const DiskQuotaCard = observer(() => {
    const { token } = theme.useToken()
    const line = currentLine()

    if (line === null) return null

    const used = line.usedBytes + line.reservedBytes
    const limit = line.quotaBytes
    const percent = limit !== null && limit > 0 ? Math.min(100, (used / limit) * 100) : null
    const warn = percent !== null && percent >= DISK_QUOTA_WARN_PERCENT
    const title = line.type === 'personal' ? 'Мій диск' : line.title

    return (
        <section className={classNames(styles['quota'])} aria-label="Місце на диску" data-testid="disk-quota">
            <div className={classNames(styles['quotaHead'])}>
                <TbDatabase />
                <span className={classNames(styles['quotaTitle'])}>Місце · {title}</span>
                {percent !== null && <span>{Math.round(percent)} %</span>}
            </div>
            {percent !== null && (
                <Progress
                    percent={percent}
                    showInfo={false}
                    size="small"
                    strokeColor={warn ? (percent >= 95 ? token.colorError : token.colorWarning) : token.colorPrimary}
                />
            )}
            <div className={classNames(styles['quotaMeta'])}>
                {limit !== null
                    ? `Використано ${formatBytes(used)} з ${formatBytes(limit)}`
                    : `Використано ${formatBytes(used)}, окремої межі немає`}
            </div>
            {warn && (
                <div className={classNames(styles['quotaWarn'])} role="status">
                    Місце закінчується — звільніть кошик або видаліть зайві файли.
                </div>
            )}
        </section>
    )
})
